import type { TextColors } from '../components/TempColorPicker';
import { newSeed } from '../chaos/rng';

export type { TextColors };

export interface ImagePlacement { x: number; y: number; width: number; height: number; rotation: number }

export interface Entry {
  id: string;
  date: string;
  location: string;
  text: string[];
  images: Blob[];
  imageLayout: ImagePlacement[] | null;
  textColors: TextColors;
  seed: number;
  preview: Blob | null;
  createdAt: string;
  updatedAt: string;
}

export interface Draft {
  editingId: string | null;
  date: string;
  location: string;
  journalText: string;
  images: string[];
  imageLayout: ImagePlacement[] | null;
  textColors: TextColors;
  section: number;
  updatedAt: string;
}

// Drafts are keyed per target so a new entry and an edit never share (or clobber) a slot.
export function draftKey(editingId: string | null): string {
  return editingId ? `edit:${editingId}` : 'new';
}

export const DEFAULT_COLORS: TextColors = { locationColor: '#FFFFFF', locationShadowColor: '#000000' };

export function newEntryId(): string {
  return `entry_${Date.now()}_${newSeed().toString(36)}`;
}

export function dataUrlToBlob(url: string): Blob | null {
  const m = /^data:([^;,]+)(;base64)?,(.*)$/s.exec(url);
  if (!m) return null;
  try {
    if (!m[2]) return new Blob([decodeURIComponent(m[3])], { type: m[1] });
    const bin = atob(m[3]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: m[1] });
  } catch {
    return null;
  }
}

export function blobToDataUrl(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });
}

const isStr = (v: unknown): v is string => typeof v === 'string';

export function legacyToEntry(raw: unknown, seed: number): Entry | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isStr(r.date) || Number.isNaN(Date.parse(r.date))) return null;
  const text = Array.isArray(r.text) ? r.text.filter(isStr) : isStr(r.text) ? [r.text] : [];
  const images = (Array.isArray(r.images) ? r.images : [])
    .filter(isStr).map(dataUrlToBlob).filter((b): b is Blob => b !== null);
  const tc = r.textColors as Partial<TextColors> | undefined;
  const textColors: TextColors = {
    locationColor: isStr(tc?.locationColor) ? tc!.locationColor : DEFAULT_COLORS.locationColor,
    locationShadowColor: isStr(tc?.locationShadowColor) ? tc!.locationShadowColor : DEFAULT_COLORS.locationShadowColor,
  };
  const createdAt = isStr(r.createdAt) ? r.createdAt : r.date;
  return {
    id: isStr(r.id) ? r.id : newEntryId(),
    date: r.date,
    location: isStr(r.location) ? r.location : '',
    text,
    images,
    imageLayout: null,
    textColors,
    seed,
    preview: isStr(r.preview) ? dataUrlToBlob(r.preview) : null,
    createdAt,
    updatedAt: createdAt,
  };
}

export function sameDay(aIso: string, bIso: string): boolean {
  const a = new Date(aIso), b = new Date(bIso);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
